import React, { useEffect, useState } from 'react';
import {
  Radar, Building2, MapPin, TrendingUp, ChevronDown, ChevronUp,
  Filter, Loader2, Briefcase, UserCheck, Layers, Zap, Users, Star,
} from 'lucide-react';
import { IntelligenceLayout, fetchWithAuth, MetricCard, useCompanyFilters, CompanyFilterBar, CompanyTypeBadge } from './IntelligenceLayout';
import { WatchlistButton } from '../components/WatchlistButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExpansionBadge = ({ score, max }) => {
  const pct = (score / max) * 100;
  let color = 'bg-gray-100 text-gray-600 border-gray-200';
  if (pct >= 75) color = 'bg-emerald-50 text-emerald-700 border-emerald-200';
  else if (pct >= 50) color = 'bg-blue-50 text-blue-700 border-blue-200';
  else if (pct >= 25) color = 'bg-amber-50 text-amber-700 border-amber-200';
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-sm font-bold border ${color}`} data-testid="expansion-score-badge">
      <Radar className="h-3.5 w-3.5" />{score}/{max}
    </span>
  );
};

const SignalPills = ({ item }) => {
  const signals = [];
  if (item.hiring_velocity_30d >= 5) signals.push({ label: `${item.hiring_velocity_30d} in 30d`, color: 'bg-blue-50 text-blue-700 border-blue-200' });
  if (item.multi_department) signals.push({ label: 'Multi-Dept', color: 'bg-violet-50 text-violet-700 border-violet-200' });
  if (item.has_leadership_hires) signals.push({ label: 'Leadership Hire', color: 'bg-amber-50 text-amber-700 border-amber-200' });
  if (item.location_expansion) signals.push({ label: 'Expanding Locations', color: 'bg-green-50 text-green-700 border-green-200' });
  if (item.rapid_growth) signals.push({ label: 'Rapid Growth', color: 'bg-red-50 text-red-700 border-red-200' });
  return (
    <div className="flex flex-wrap gap-1">
      {signals.map(s => <span key={s.label} className={`px-2 py-0.5 rounded text-[10px] font-medium border ${s.color}`}>{s.label}</span>)}
    </div>
  );
};

const JobLeadDiscovery = ({ embedded = false }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minScore, setMinScore] = useState(0);
  const [sortField, setSortField] = useState('expansion_score');
  const [sortDir, setSortDir] = useState('desc');
  const [showEmerging, setShowEmerging] = useState(false);
  const { excludeStaffing, setExcludeStaffing, excludeRetail, setExcludeRetail, filterParams } = useCompanyFilters();

  useEffect(() => {
    setLoading(true);
    fetchWithAuth(`${API_URL}/api/intelligence/job-leads?min_score=${minScore}&limit=750${filterParams}`)
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [minScore, filterParams]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const filtered = showEmerging ? data.filter(d => d.is_emerging_opportunity) : data;
  const sorted = [...filtered].sort((a, b) => {
    const mul = sortDir === 'desc' ? -1 : 1;
    if (sortField === 'company') return mul * a.company.localeCompare(b.company);
    return mul * ((a[sortField] || 0) - (b[sortField] || 0));
  });

  const highExpansion = data.filter(d => d.expansion_score >= 8).length;
  const emergingCount = data.filter(d => d.is_emerging_opportunity).length;
  const leadershipCount = data.filter(d => d.has_leadership_hires).length;
  const multiDeptCount = data.filter(d => d.multi_department).length;

  const SortHeader = ({ field, label, align = 'left' }) => (
    <th className={`text-${align} px-4 py-3 cursor-pointer hover:text-[#0A1F44] select-none whitespace-nowrap`} onClick={() => handleSort(field)}>
      <span className="inline-flex items-center gap-1">{label}
        {sortField === field && (sortDir === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
      </span>
    </th>
  );

  return (
    <IntelligenceLayout title="Job Leads" embedded={embedded}>
      <header className="bg-white border-b border-gray-200 px-8 py-5 shadow-sm">
        <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="job-leads-title">Job Lead Discovery Engine</h1>
        <p className="text-sm text-gray-500">Companies likely hiring or expanding — early recruiting opportunities before competitors</p>
      </header>

      <main className="p-8 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-2 text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /> Scanning hiring expansion signals...</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="lead-metrics">
              <MetricCard label="Total Leads" value={data.length} icon={Building2} />
              <MetricCard label="High Expansion (8+)" value={highExpansion} icon={Radar} />
              <MetricCard label="Emerging Opportunities" value={emergingCount} icon={Star} />
              <MetricCard label="Leadership Hires" value={leadershipCount} icon={UserCheck} />
            </div>

            <CompanyFilterBar excludeStaffing={excludeStaffing} setExcludeStaffing={setExcludeStaffing} excludeRetail={excludeRetail} setExcludeRetail={setExcludeRetail} />

            <div className="flex flex-wrap items-center gap-4" data-testid="lead-filters">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-600 font-medium">Min Score:</span>
              </div>
              {[0, 4, 6, 8, 10].map(s => (
                <button key={s} onClick={() => setMinScore(s)} data-testid={`lead-filter-${s}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${minScore === s ? 'bg-[#D4AF37] text-[#0A1F44] shadow-sm' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}>
                  {s === 0 ? 'All' : `${s}+`}
                </button>
              ))}
              <span className="text-gray-300 mx-1">|</span>
              <button onClick={() => setShowEmerging(!showEmerging)} data-testid="toggle-emerging"
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${showEmerging ? 'bg-emerald-600 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}>
                <Star className="h-3 w-3" /> Emerging Only ({emergingCount})
              </button>
              <span className="text-xs text-gray-400">({sorted.length} companies)</span>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm" data-testid="leads-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 uppercase border-b border-gray-100 bg-gray-50">
                      <SortHeader field="company" label="Company" />
                      <SortHeader field="expansion_score" label="Expansion Score" align="center" />
                      <SortHeader field="total_postings" label="Open Roles" align="right" />
                      <th className="text-left px-4 py-3">Departments</th>
                      <SortHeader field="hiring_velocity_30d" label="30d Velocity" align="right" />
                      <th className="text-left px-4 py-3">Signals</th>
                      <th className="text-left px-4 py-3">Recommended Contacts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.length === 0 ? (
                      <tr><td colSpan={7} className="text-center py-12 text-gray-400">No leads match the current filters.</td></tr>
                    ) : sorted.map((item, i) => (
                      <tr key={item.company} className={`border-t border-gray-100 transition-colors ${item.is_emerging_opportunity ? 'bg-emerald-50/30 hover:bg-emerald-50/50' : 'hover:bg-[#f8f6f0]'}`} data-testid={`lead-row-${i}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <WatchlistButton company={item.company} sourceModule="job-leads" />
                            {item.is_emerging_opportunity && <Star className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />}
                            <div>
                              <div className="font-medium text-[#0A1F44]">{item.company}</div>
                              <div className="flex items-center gap-1 text-[10px] text-gray-400 mt-0.5">
                                {(item.locations || []).slice(0, 2).map((loc, j) => (
                                  <span key={j} className="flex items-center gap-0.5"><MapPin className="h-2.5 w-2.5" />{loc}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center"><ExpansionBadge score={item.expansion_score} max={item.max_score} /></td>
                        <td className="px-4 py-3 text-right font-semibold text-[#0A1F44]">{item.total_postings}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(item.departments_hiring || []).slice(0, 4).map(d => (
                              <span key={d} className="px-2 py-0.5 rounded text-[10px] bg-violet-50 text-violet-700 border border-violet-200">{d}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`font-medium ${item.hiring_velocity_30d >= 10 ? 'text-blue-600' : 'text-gray-600'}`}>
                            {item.hiring_velocity_30d}
                          </span>
                        </td>
                        <td className="px-4 py-3"><SignalPills item={item} /></td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(item.recommended_contact_titles || []).map(t => (
                              <span key={t} className="px-2 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600 border border-gray-200">{t}</span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="expansion-legend">
              <h3 className="text-sm font-semibold text-[#0A1F44] mb-3 uppercase tracking-wider">Hiring Expansion Scoring (Max 13 pts)</h3>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                {[
                  { label: '5+ Postings in 30 Days', pts: '+3', icon: Briefcase, color: 'text-blue-600' },
                  { label: 'Multi-Department Hiring', pts: '+3', icon: Layers, color: 'text-violet-600' },
                  { label: 'Leadership Hires', pts: '+3', icon: UserCheck, color: 'text-amber-600' },
                  { label: 'Location Expansion', pts: '+2', icon: MapPin, color: 'text-green-600' },
                  { label: 'Rapid Recent Growth', pts: '+2', icon: Zap, color: 'text-red-600' },
                ].map(c => (
                  <div key={c.label} className="flex items-center gap-2 text-sm">
                    <c.icon className={`h-4 w-4 ${c.color}`} />
                    <span className="text-gray-600">{c.label}</span>
                    <span className="ml-auto font-bold text-[#0A1F44]">{c.pts}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </main>
    </IntelligenceLayout>
  );
};

export default JobLeadDiscovery;
